import { useEffect, useState, useCallback } from "react";
import { X, Trophy } from "lucide-react";

const ICON_MAP = {
  gamepad: "🎮", trophy: "🏆", star: "⭐", crown: "👑",
  "check-circle": "✅", zap: "⚡", flame: "🔥", award: "🥇",
  gem: "💎", globe: "🌍", heart: "❤️", lightbulb: "💡",
  sparkles: "✨", "help-circle": "❓", clipboard: "📋",
  "book-open": "📖", "pen-tool": "✍️", timer: "⏱️", rocket: "🚀",
};

// CSS Confetti particles
const ConfettiParticle = ({ delay, left, color }) => (
  <div
    className="absolute w-2 h-2 rounded-full animate-confetti"
    style={{
      left: `${left}%`,
      backgroundColor: color,
      animationDelay: `${delay}ms`,
    }}
  />
);

const CONFETTI_COLORS = ["#a855f7", "#c084fc", "#e879f9", "#f0abfc", "#fccf1f", "#facc15"];

export default function AchievementNotification({ achievement, onClose }) {
  const [visible, setVisible] = useState(false);
  const [particles] = useState(() =>
    Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 500,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    }))
  );

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => onClose?.(), 200);
  }, [onClose]);

  useEffect(() => {
    // Trigger entrance animation
    requestAnimationFrame(() => setVisible(true));
    
    // Handle ESC key
    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  if (!achievement) return null;

  const icon = ICON_MAP[achievement.icon] || "🏅";

  return (
    <>
      {/* Confetti animation styles */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(-20px) rotate(0deg) scale(1);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg) scale(0);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti-fall 2s ease-out forwards;
        }
        @keyframes achievement-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(168, 85, 247, 0.4); }
          50% { box-shadow: 0 0 40px rgba(168, 85, 247, 0.8); }
        }
        .animate-achievement-pulse {
          animation: achievement-pulse 1.5s ease-in-out 2;
        }
      `}</style>

      {/* Overlay backdrop */}
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="achievement-title"
        data-testid="achievement-notification-overlay"
      >
        {/* Confetti particles */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          {particles.map((p) => (
            <ConfettiParticle key={p.id} {...p} />
          ))}
        </div>

        {/* Backdrop blur */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        {/* Notification card */}
        <div
          className={`relative bg-gradient-to-br from-purple-900/95 via-purple-800/95 to-violet-900/95 
            border-2 border-purple-400/50 rounded-2xl p-6 sm:p-8 max-w-sm w-full mx-4
            shadow-2xl transform transition-all duration-300 animate-achievement-pulse
            ${visible ? "scale-100 opacity-100" : "scale-90 opacity-0"}`}
          onClick={(e) => e.stopPropagation()}
          data-testid="achievement-notification-card"
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 text-purple-300 hover:text-white transition-colors p-1 rounded-full hover:bg-purple-700/50"
            aria-label="Fechar notificação"
            data-testid="achievement-notification-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Trophy glow effect */}
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-20 h-20 bg-purple-500/30 rounded-full blur-xl" />

          {/* Content */}
          <div className="text-center relative z-10">
            {/* Header */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <Trophy className="w-5 h-5 text-purple-300" />
              <h2
                id="achievement-title"
                className="text-sm font-bold uppercase tracking-wider text-purple-300"
              >
                Conquista Desbloqueada
              </h2>
              <Trophy className="w-5 h-5 text-purple-300" />
            </div>

            {/* Icon */}
            <div className="text-6xl mb-4 drop-shadow-lg animate-bounce">
              {icon}
            </div>

            {/* Achievement name */}
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2 font-nunito">
              {achievement.name}
            </h3>

            {/* Description */}
            {achievement.desc && (
              <p className="text-purple-200 text-sm sm:text-base mb-6 font-outfit">
                {achievement.desc}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
